import type { ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import { cn } from '@sara/ui/lib/utils';

interface MarkdownTextProps {
  children: string;
  className?: string;
  /**
   * When true, render with a `<span>` wrapper and inline-only constructs
   * (bold + line breaks). Use when MarkdownText sits inside phrasing content
   * (e.g. a `<label>`). Defaults to false (block).
   */
  inline?: boolean;
}

/**
 * Defeats remark's ATX-heading parser for lines beginning `#{1,6} ` so a
 * `## heading` in authored consent copy renders as literal text, not an <h2>.
 */
function escapeHeadings(markdown: string): string {
  return markdown
    .split('\n')
    .map((line) => (/^#{1,6} /.test(line) ? `\\${line}` : line))
    .join('\n');
}

/**
 * Renders the same constrained Markdown subset the interview app's consent
 * point uses (paragraphs, line breaks, **bold**, `- ` lists; headings, links,
 * code, images, and tables are stripped to plain text). Mirrors
 * apps/interview/src/components/markdown-text.tsx so the console consent preview
 * matches what the candidate will actually see. Kept local because the design
 * system (`@sara/ui`) is out of this change's scope.
 */
export function MarkdownText({ children, className, inline = false }: MarkdownTextProps) {
  const Wrapper = inline ? 'span' : 'div';
  const allowedElements = inline ? ['strong', 'em', 'br'] : ['p', 'br', 'strong', 'em', 'ul', 'li'];
  const components = inline
    ? {
        p: ({ children }: { children?: ReactNode }) => children,
      }
    : undefined;

  return (
    <Wrapper className={cn(className)}>
      <ReactMarkdown allowedElements={allowedElements} unwrapDisallowed components={components}>
        {escapeHeadings(children)}
      </ReactMarkdown>
    </Wrapper>
  );
}
